// Outer-circle / nested-rings detector.
//
// Given an SVG with one or more concentric rings (and possibly inner clutter
// like runes), answer:
//   1. all the rings — center, radius, circularity, inlier subpath indices
//   2. which subpaths are "leftover" (inside the rings, candidates for symbols)
//   3. the outermost ring as the primary result (backwards compatible)
//
// Algorithm — iterative ("sequential peeling"):
//   1. Parse SVG → subpaths
//   2. Densify each subpath uniformly along arc length
//   3. Pool all points (tagged with their subpath index)
//   4. Repeat up to MAX_RINGS times:
//        a. RANSAC across the CURRENT pool → find best circle fit
//        b. if no valid fit, break
//        c. record this ring (center, radius, circularity, subpath indices)
//        d. peel: remove this fit's inlier points from the pool
//   5. Sort discovered rings by radius descending — largest first
//   6. Tag remaining (un-peeled) subpaths as "candidate symbols"
//   7. Return the outermost ring as the primary result, plus allRings[] and
//      remainingSubpathIndices[] for downstream consumers
//
// Returns { isCircle, circularity, center, radius, inlierSubpathIndices,
//           allRings: [...], remainingSubpathIndices: [...], ... }

import { parseSVG } from './svg-parse.js';

const CIRCULARITY_THRESHOLD = 0.85;
const RANSAC_ITERS = 400;
const RANSAC_INLIER_BAND = 0.06;          // fraction of bbox diagonal — wider than the strict 0.04 to accommodate excalidraw's hand-drawn "rough" rendering, which jitters strokes by ~5-10% of the radius
const SUBPATH_INLIER_FRACTION = 0.5;      // a subpath is an "outer ring participant" if this much of it is on the circle
const DENSIFY_PER_SUBPATH = 64;
const MIN_RADIUS_FRACTION = 0.06;         // reject RANSAC fits whose radius is < this fraction of bbox diagonal. Was 0.15 but that rejected inner concentric rings (which can be ~1/10th the outer radius); 0.06 still kills near-collinear degenerate fits while admitting inner rings
const MAX_RADIUS_FACTOR = 1.5;            // reject RANSAC fits whose radius is > this × bbox diagonal (kills near-collinear degenerate fits)
const MAX_RINGS = 5;                      // upper bound on detected concentric rings (sanity cap; spell-circle drawings rarely have more)
const MIN_REMAINING_POINTS = 12;          // below this, peeling stops — not enough points to fit another circle
const RING_PEEL_CIRCULARITY = 0.7;        // peeling stops when the next discovered ring's circularity drops below this. keeps RANSAC from accepting "circle fits" through star polygons / inner dots after the real rings are exhausted. lower than CIRCULARITY_THRESHOLD (0.85) so wobbly hand-drawn inner rings still pass.

/**
 * @param {string} svgString
 * @param {Object} opts — see constants above for defaults
 *   - maxRings (number, default 5): cap on rings to discover via peeling
 * @returns detection of the OUTERMOST ring + an allRings[] array sorted
 *          outermost → innermost, + remainingSubpathIndices[] for content
 *          that's inside the rings but isn't itself a ring.
 */
export function detectOuterCircle(svgString, opts = {}) {
  const threshold = opts.threshold ?? CIRCULARITY_THRESHOLD;
  const ransacIters = opts.ransacIters ?? RANSAC_ITERS;
  const inlierBand = opts.inlierBand ?? RANSAC_INLIER_BAND;
  const subpathInlierFrac = opts.subpathInlierFraction ?? SUBPATH_INLIER_FRACTION;
  const minRadiusFrac = opts.minRadiusFraction ?? MIN_RADIUS_FRACTION;
  const maxRings = opts.maxRings ?? MAX_RINGS;
  const peelMinCirc = opts.ringPeelCircularity ?? RING_PEEL_CIRCULARITY;

  const parsed = parseSVG(svgString, opts);
  if (parsed.subpaths.length === 0) {
    return nullResult('no subpaths in SVG');
  }

  const densifiedSubpaths = parsed.subpaths.map((sp) =>
    densifyPolyline(sp, opts.densifyN ?? DENSIFY_PER_SUBPATH),
  );

  const pool = [];
  for (let i = 0; i < densifiedSubpaths.length; i++) {
    for (const p of densifiedSubpaths[i]) {
      pool.push({ x: p.x, y: p.y, sp: i });
    }
  }
  if (pool.length < 3) {
    return nullResult('not enough points for circle fit');
  }

  const bboxDiag = Math.sqrt(
    parsed.bbox.w * parsed.bbox.w + parsed.bbox.h * parsed.bbox.h,
  ) || 1;
  const inlierThresh = inlierBand * bboxDiag;
  const minRadius = minRadiusFrac * bboxDiag;
  const maxRadius = (opts.maxRadiusFactor ?? MAX_RADIUS_FACTOR) * bboxDiag;

  // ---- Sequential peeling: find each ring, then remove its inliers ----
  let activePool = pool.slice();
  const rings = [];

  for (let ringNum = 0; ringNum < maxRings; ringNum++) {
    if (activePool.length < MIN_REMAINING_POINTS) break;

    const fit = ransacCircle(activePool, {
      iters: ransacIters,
      inlierThresh,
      minRadius,
      maxRadius,
      bboxDiag,
    });
    if (!fit) break;

    const ring = buildRingResult(
      fit, activePool, densifiedSubpaths, threshold, subpathInlierFrac,
    );

    // Stop peeling when the next "ring" isn't actually circular. This
    // prevents spurious circle-fits through inner content (star polygons,
    // tangent point clusters) from being recorded as rings and stealing
    // subpath ownership from the "remaining" content.
    if (ring.circularity < peelMinCirc) break;

    rings.push(ring);

    // Peel: drop inlier points from the active pool for the next iteration.
    const inlierSet = new Set(fit.inlierIdx);
    activePool = activePool.filter((_, i) => !inlierSet.has(i));
  }

  // No rings found at all → fall back to largest-bbox subpath fit.
  if (rings.length === 0) {
    return fallbackLargestSubpath(densifiedSubpaths, parsed.subpaths, threshold);
  }

  // Sort outermost → innermost.
  rings.sort((a, b) => b.radius - a.radius);

  // Subpaths that didn't claim membership in ANY ring are "remaining" content
  // (i.e., candidates for inner symbols / runes).
  const claimedSubpaths = new Set();
  for (const ring of rings) {
    for (const sp of ring.inlierSubpathIndices) claimedSubpaths.add(sp);
  }
  const remainingSubpathIndices = [];
  for (let i = 0; i < densifiedSubpaths.length; i++) {
    if (!claimedSubpaths.has(i)) remainingSubpathIndices.push(i);
  }

  // Primary result = outermost ring, with the multi-ring data attached.
  const outer = rings[0];
  return {
    ...outer,
    totalPointCount: pool.length,
    subpathCount: densifiedSubpaths.length,
    allRings: rings,
    remainingSubpathIndices,
    ringCount: rings.length,
  };
}

/**
 * Turn a RANSAC fit (with its active-pool inlier indices) into a full ring
 * result object. Shared between the first-pass and subsequent peeling iters.
 */
function buildRingResult(fit, activePool, densifiedSubpaths, threshold, subpathInlierFrac) {
  const inlierPoints = fit.inlierIdx.map((i) => activePool[i]);
  const refined = fitCircleKasa(inlierPoints) || {
    cx: fit.cx,
    cy: fit.cy,
    r: fit.r,
  };

  const subpathInlierCount = new Array(densifiedSubpaths.length).fill(0);
  for (const idx of fit.inlierIdx) {
    subpathInlierCount[activePool[idx].sp]++;
  }
  const inlierSubpathIndices = [];
  for (let i = 0; i < densifiedSubpaths.length; i++) {
    const total = densifiedSubpaths[i].length;
    if (total > 0 && subpathInlierCount[i] / total >= subpathInlierFrac) {
      inlierSubpathIndices.push(i);
    }
  }

  const { rmse, circularity: residualCirc } = residualCircularity(inlierPoints, refined);
  const isoCirc = isoperimetricForInlierSubpaths(densifiedSubpaths, inlierSubpathIndices);
  const combined = Math.min(residualCirc, isoCirc);

  return {
    isCircle: combined >= threshold,
    circularity: combined,
    center: { x: refined.cx, y: refined.cy },
    radius: refined.r,
    residualCircularity: residualCirc,
    isoperimetricRatio: isoCirc,
    inlierRatio: fit.inlierIdx.length / activePool.length,
    rmse,
    inlierSubpathIndices,
    inlierCount: fit.inlierIdx.length,
  };
}

/**
 * Fallback when RANSAC fails: fit a circle to the single largest-bbox subpath
 * (the previous algorithm). circularity will typically be low — we report it
 * so the visual layer can still highlight what the algorithm picked as "outer
 * object".
 */
function fallbackLargestSubpath(densifiedSubpaths, originalSubpaths, threshold) {
  let bestIdx = 0, bestArea = -Infinity;
  for (let i = 0; i < densifiedSubpaths.length; i++) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of densifiedSubpaths[i]) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }
    const area = (maxX - minX) * (maxY - minY);
    if (area > bestArea) { bestArea = area; bestIdx = i; }
  }
  const outer = densifiedSubpaths[bestIdx];
  const fit = fitCircleKasa(outer);
  if (!fit) return nullResult('fallback fit failed');

  const { rmse, circularity: residualCirc } = residualCircularity(outer, fit);
  const isoCirc = isoperimetricCircularity(outer);
  const combined = Math.min(residualCirc, isoCirc);

  return {
    isCircle: combined >= threshold,
    circularity: combined,
    center: { x: fit.cx, y: fit.cy },
    radius: fit.r,
    residualCircularity: residualCirc,
    isoperimetricRatio: isoCirc,
    inlierRatio: 0,
    rmse,
    inlierSubpathIndices: [bestIdx],
    inlierCount: outer.length,
    totalPointCount: densifiedSubpaths.reduce((s, sp) => s + sp.length, 0),
    subpathCount: densifiedSubpaths.length,
    fallback: true,
  };
}

function nullResult(reason) {
  return {
    isCircle: false,
    circularity: 0,
    center: { x: 0, y: 0 },
    radius: 0,
    residualCircularity: 0,
    isoperimetricRatio: 0,
    inlierRatio: 0,
    rmse: Infinity,
    inlierSubpathIndices: [],
    inlierCount: 0,
    totalPointCount: 0,
    subpathCount: 0,
    error: reason,
  };
}

/**
 * RANSAC circle search.
 *
 * Score: inlier count × angular coverage of inliers around the fit center,
 * with a small radius tiebreaker. The angular-coverage multiplier is what
 * distinguishes "a real circle whose inliers span 360°" from "a tangent
 * cluster of points that all happen to be at distance r from a fake center"
 * — which shows up when there's a small, dense subpath (e.g. an inner dot)
 * whose centroid happens to be ~r away from the fake center. Without this
 * gate, RANSAC reliably mis-locks onto such clusters.
 */
function ransacCircle(points, { iters, inlierThresh, minRadius, maxRadius, bboxDiag }) {
  const n = points.length;
  let best = null;
  for (let it = 0; it < iters; it++) {
    const i = (Math.random() * n) | 0;
    const j = (Math.random() * n) | 0;
    const k = (Math.random() * n) | 0;
    if (i === j || j === k || i === k) continue;

    const fit = circleFromThreePoints(points[i], points[j], points[k]);
    if (!fit) continue;
    if (!Number.isFinite(fit.r)) continue;
    if (fit.r < minRadius || fit.r > maxRadius) continue;

    const inlierIdx = [];
    for (let p = 0; p < n; p++) {
      const dx = points[p].x - fit.cx;
      const dy = points[p].y - fit.cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (Math.abs(d - fit.r) <= inlierThresh) inlierIdx.push(p);
    }
    if (inlierIdx.length < 3) continue;

    // Two angular sanity checks:
    //  1. Bin-coverage: at least 70% of 12 angular sectors must have
    //     meaningful inlier density. Stops "fits where inliers cluster
    //     in one direction".
    //  2. No single bin dominates: max bin must hold ≤ 25% of inliers.
    //     Stops "tangent cluster" failures where a small dense subpath
    //     happens to lie ~r away from a fake center, dumping its entire
    //     point set into one angular sector.
    const { coverage: angCov, maxBinFraction } = binCoverageStats(
      inlierIdx, points, fit.cx, fit.cy,
    );
    if (angCov < 0.7) continue;
    if (maxBinFraction > 0.25) continue;

    // Outside-point penalty (the "outermost" predicate): a candidate's
    // enclosureFactor is the fraction of the ACTIVE pool that lies inside
    // (within r + band). A true outermost-circle fit has nothing outside →
    // enclosureFactor = 1. A compromise fit between two concentric rings
    // has the outer ring's points outside it → factor drops. This is what
    // makes sequential peeling pick the *largest* ring first, then the
    // next-largest after the first one's inliers are removed.
    let outsideCount = 0;
    for (let p = 0; p < n; p++) {
      const dx = points[p].x - fit.cx;
      const dy = points[p].y - fit.cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > fit.r + inlierThresh) outsideCount++;
    }
    const enclosureFactor = 1 - outsideCount / n;

    const radiusBonus = (fit.r / bboxDiag) * 0.5;
    const score =
      inlierIdx.length * angCov * angCov * enclosureFactor + radiusBonus;
    if (!best || score > best.score) {
      best = { ...fit, inlierIdx, angCov, enclosureFactor, score };
    }
  }
  return best;
}

/**
 * Bin coverage stats: divide the full 360° around (cx, cy) into N angular
 * sectors. Returns:
 *   - coverage:        fraction of bins with ≥ minPerBin inliers
 *   - maxBinFraction:  fraction of all inliers that fall into the heaviest bin
 *
 * Both metrics are needed because they catch different pathologies:
 *   - low coverage:        tangent fits whose inliers cluster on one side
 *   - high maxBinFraction: tangent fits with stragglers around the canvas
 *                          that fill bin coverage but the real density is
 *                          piled into one direction
 */
function binCoverageStats(inlierIdx, points, cx, cy, nBins = 12, minPerBin = 2) {
  if (inlierIdx.length < 2) return { coverage: 0, maxBinFraction: 1 };
  const bins = new Array(nBins).fill(0);
  const sectorSize = (2 * Math.PI) / nBins;
  for (const idx of inlierIdx) {
    const p = points[idx];
    let a = Math.atan2(p.y - cy, p.x - cx);
    if (a < 0) a += 2 * Math.PI;
    const b = Math.min(nBins - 1, Math.floor(a / sectorSize));
    bins[b]++;
  }
  let covered = 0;
  let maxBin = 0;
  for (const c of bins) {
    if (c >= minPerBin) covered++;
    if (c > maxBin) maxBin = c;
  }
  return {
    coverage: covered / nBins,
    maxBinFraction: maxBin / inlierIdx.length,
  };
}

/**
 * Exact circle through 3 points (using perpendicular-bisector intersection).
 * Returns null if the points are colinear.
 */
function circleFromThreePoints(a, b, c) {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) return null;
  const a2 = a.x * a.x + a.y * a.y;
  const b2 = b.x * b.x + b.y * b.y;
  const c2 = c.x * c.x + c.y * c.y;
  const cx = (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d;
  const cy = (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d;
  const r = Math.sqrt((cx - a.x) * (cx - a.x) + (cy - a.y) * (cy - a.y));
  return { cx, cy, r };
}

/**
 * Kasa algebraic least-squares circle fit, mean-centered for stability.
 */
export function fitCircleKasa(points) {
  const n = points.length;
  if (n < 3) return null;

  let mx = 0, my = 0;
  for (const p of points) { mx += p.x; my += p.y; }
  mx /= n; my /= n;

  let Suu = 0, Svv = 0, Suv = 0, Suuu = 0, Svvv = 0, Suvv = 0, Svuu = 0;
  for (const p of points) {
    const u = p.x - mx, v = p.y - my;
    const u2 = u * u, v2 = v * v;
    Suu += u2;
    Svv += v2;
    Suv += u * v;
    Suuu += u2 * u;
    Svvv += v2 * v;
    Suvv += u * v2;
    Svuu += v * u2;
  }

  const det = Suu * Svv - Suv * Suv;
  if (Math.abs(det) < 1e-12) return null;

  const rhsU = (Suuu + Suvv) / 2;
  const rhsV = (Svvv + Svuu) / 2;
  const a = (Svv * rhsU - Suv * rhsV) / det;
  const b = (Suu * rhsV - Suv * rhsU) / det;

  const cx = a + mx;
  const cy = b + my;
  const r = Math.sqrt(a * a + b * b + (Suu + Svv) / n);

  return { cx, cy, r };
}

function residualCircularity(points, fit) {
  const { cx, cy, r } = fit;
  let sse = 0;
  for (const p of points) {
    const d = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
    const e = d - r;
    sse += e * e;
  }
  const rmse = Math.sqrt(sse / points.length);
  const circularity = r === 0 ? 0 : Math.max(0, Math.min(1, 1 - rmse / r));
  return { rmse, circularity };
}

/**
 * For the inlier subpaths, compute an isoperimetric ratio that asks
 * "do these strokes together cover the whole circle?" by:
 *   1. Concatenating points
 *   2. Sorting by polar angle around centroid
 *   3. Treating the result as a closed polygon and computing 4πA/P²
 *
 * This rewards full coverage of the outer ring even when drawn as multiple arcs.
 */
function isoperimetricForInlierSubpaths(subpaths, indices) {
  if (indices.length === 0) return 0;
  const all = [];
  for (const i of indices) all.push(...subpaths[i]);
  if (all.length < 3) return 0;

  // Centroid
  let cx = 0, cy = 0;
  for (const p of all) { cx += p.x; cy += p.y; }
  cx /= all.length; cy /= all.length;

  // Sort by angle around centroid → forms a closed polygon
  const sorted = all
    .map((p) => ({ ...p, a: Math.atan2(p.y - cy, p.x - cx) }))
    .sort((u, v) => u.a - v.a);

  return isoperimetricCircularity(sorted);
}

function isoperimetricCircularity(points) {
  const n = points.length;
  if (n < 3) return 0;
  let area2 = 0, perim = 0;
  for (let i = 0; i < n; i++) {
    const p = points[i], q = points[(i + 1) % n];
    area2 += p.x * q.y - q.x * p.y;
    perim += Math.sqrt((p.x - q.x) ** 2 + (p.y - q.y) ** 2);
  }
  const area = Math.abs(area2) / 2;
  if (perim === 0) return 0;
  return Math.min(1, (4 * Math.PI * area) / (perim * perim));
}

function densifyPolyline(points, n) {
  if (points.length === 0) return [];
  if (points.length === 1) return Array.from({ length: n }, () => ({ ...points[0] }));

  let totalLen = 0;
  const segLens = new Array(points.length - 1);
  for (let i = 1; i < points.length; i++) {
    const d = Math.sqrt((points[i].x - points[i - 1].x) ** 2 + (points[i].y - points[i - 1].y) ** 2);
    segLens[i - 1] = d;
    totalLen += d;
  }
  if (totalLen === 0) return Array.from({ length: n }, () => ({ ...points[0] }));

  const out = [];
  const step = totalLen / (n - 1);
  let segIdx = 0;
  let walked = 0;          // cumulative arc length up to (but not including) segIdx
  out.push({ ...points[0] });
  for (let i = 1; i < n - 1; i++) {
    const target = step * i;
    while (segIdx < segLens.length && walked + segLens[segIdx] < target) {
      walked += segLens[segIdx];
      segIdx++;
    }
    if (segIdx >= segLens.length) {
      out.push({ ...points[points.length - 1] });
      continue;
    }
    const t = (target - walked) / segLens[segIdx];
    const a = points[segIdx], b = points[segIdx + 1];
    out.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
  }
  out.push({ ...points[points.length - 1] });
  return out;
}
